import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, Fragment } from 'react';

import {
Box,
Typography,
Grid,
Card,
CardContent,
Button,
CircularProgress,
ToggleButton,
ToggleButtonGroup,
Chip,
Alert,
FormControl,
InputLabel,
Select,
MenuItem,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
} from '@mui/material';
import {
  LineChart, Line,
  XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine,
  BarChart, Bar, Cell, LabelList, Customized
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import * as htmlToImage from 'html-to-image';
import dayjs from "dayjs";

const ExFacSummaries = forwardRef(function ExFacSummaries(
  { apiUrl, showAskRxAI, onAskRxAI, selectedDate },
  ref
) {
  // ---------------- UI helpers & palette ----------------
  const LINE_PALETTE = ['#1f77b4','#ff7f0e','#2ca02c','#d62728'];
  const FORECAST_COLOR = '#2e7d32';
  const YTD_COLOR = '#1565c0';
  const BUDGET_COLOR = '#9c27b0';
  const WHOLESALER_COLORS = {
    ALLIANCE: '#1F4B8E',
    AAH:      '#0F766E',
    PRINWEST: '#6B7280',
    PHOENIX:  '#7C3AED',
    ETHIGEN:  '#9CA3AF',
    OTHER:    '#A3A3A3'
  };
  const whColor = (name) => WHOLESALER_COLORS[name] || WHOLESALER_COLORS.OTHER;

  const ordinal = (n) => { const s=['th','st','nd','rd'], v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
  const fmtGBP = (n) => `£${Math.round(n||0).toLocaleString('en-GB')}`;
  const fmtUnits = (n) => `${Math.round(n||0).toLocaleString('en-GB')}`;
  const fmtGBPShort = (n) =>
    '£' + Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 }).format(Math.round(n || 0));
  const fmtUnitsShort = (n) =>
    Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 }).format(Math.round(n || 0));
  const labelFmt = (iso) => {
    try {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('en-GB', { month: 'short', day: '2-digit' });
    } catch { return iso; }
  };

  function SectionCard({ title, subtitle, right, children, sx }) {
    return (
      <Card sx={{ borderRadius:2, border:'1px solid', borderColor:'divider', boxShadow:'0 4px 14px rgba(0,0,0,0.04)', ...sx }}>
        <CardContent sx={{ p: 2.5 }}>
          {(title || right || subtitle) && (
            <Box sx={{ mb: 1.5, display:'flex', alignItems:'baseline', justifyContent:'space-between' }}>
              <Box>
                {title && <Typography variant="h6" sx={{ fontWeight:700 }}>{title}</Typography>}
                {subtitle && <Typography variant="body2" sx={{ opacity:0.7, mt:0.25 }}>{subtitle}</Typography>}
              </Box>
              {right}
            </Box>
          )}
          {children}
        </CardContent>
      </Card>
    );
  }

  const [seriesKey, setSeriesKey] = useState('portfolio_cash');
  const [asOf, setAsOf] = React.useState('');
  const [title, setTitle] = React.useState('Cumulative Daily');
  const [actualSales, setActualSales] = React.useState(0);
  const [metric, setMetric] = React.useState('value'); // value | quantity
  const isCash = metric === 'value';
  const [expectedRemaining, setExpectedRemaining] = React.useState({});

  const [data, setData] = React.useState([]);
  const [eomForecast, setEOMForecast] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const [ytdData, setYtdData] = React.useState([]);
  const [ytdAsOf, setYtdAsOf] = React.useState('');
  const [ytdMetric, setYtdMetric] = React.useState('value');
  const [ytdTotals, setYtdTotals] = React.useState({ ytd_total: 0, eoy_total_forecast: 0 });
  const [ytdLoading, setYtdLoading] = React.useState(false);
  const [ytdError, setYtdError] = React.useState('');
  const [monthlyBudgets, setMonthlyBudgets] = React.useState([]);
  const [fyBudget, setFYBudget] = React.useState(0);
  const [eomChartPayload, setEomChartPayload] = useState(null);        // monthly_forecast payload
  const [expectedOrdersPayload, setExpectedOrdersPayload] = useState(null); // ytd_forecast payload

  const [expRows, setExpRows] = React.useState([]);
  const [expMeta, setExpMeta] = React.useState(null);
  const [expLoading, setExpLoading] = React.useState(false);
  const [expError, setExpError] = React.useState('');

    // Rx AI Summary (EOM insight)
  const [eomInsight, setEomInsight] = React.useState('');
  const [eomInsightLoading, setEomInsightLoading] = React.useState(false);
  const [eomInsightError, setEomInsightError] = React.useState('');
  const [eomWatchTargets, setEomWatchTargets] = React.useState([]);

    // Rx AI Monitoring – active items selected by the user
  const [monitors, setMonitors] = React.useState([]);
  
    // --- Rx AI Monitoring state ---
  const [monitorItems, setMonitorItems] = useState([]); 
  // each: { id, type, code, label, insight }
  const [monitorLoadingIds, setMonitorLoadingIds] = useState([]);

    // --- Backend-backed Rx AI Monitoring snapshot ---
  const [monitorSnapshot, setMonitorSnapshot] = useState([]);
  const [monitorSnapshotLoading, setMonitorSnapshotLoading] = useState(false);
  const [monitorSnapshotError, setMonitorSnapshotError] = useState('');

    // Portfolio-level monitoring view (gap vs budget vs projection) + AI commentary
  const [monitorPortfolio, setMonitorPortfolio] = useState(null);
  const [monitorPortfolioComment, setMonitorPortfolioComment] = useState('');
  // Daily bullet headlines derived from AI
  const [monitorDailyHeadlines, setMonitorDailyHeadlines] = useState([]);

    // Portfolio monitoring view helpers
  const [showFullPortfolioComment, setShowFullPortfolioComment] = useState(false);

  // Collapsible RAG buckets in Monitoring tab
  const [openBuckets, setOpenBuckets] = useState({
    High: true,
    Medium: true,
    Low: true,
  });

  const [activeMonitorDetail, setActiveMonitorDetail] = useState(null);

    // Risk detail overlay
  const [riskDetailOpen, setRiskDetailOpen] = useState(false);
  const [riskDetailItem, setRiskDetailItem] = useState(null);

  const [aiOpen, setAiOpen] = React.useState(false);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiThreadId, setAiThreadId] = React.useState(null);
  const [aiMsgs, setAiMsgs] = React.useState([{ role: 'assistant', content: 'Welcome to RxAI. Ask about anything on the page.' }]);
  const [aiInput, setAiInput] = React.useState('');
  const [aiError, setAiError] = React.useState('');
  const [exportMode, setExportMode] = React.useState(false);
  

  const [expectedInsight, setExpectedInsight] = useState('');
  const [expectedInsightLoading, setExpectedInsightLoading] = useState(false);
  const [expectedInsightError, setExpectedInsightError] = useState('');

    // Main view switch: dashboard vs Rx AI Monitoring
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' | 'monitoring'

    // Parse Rx AI EOM summary into labelled sections
  const parsedEomSections = React.useMemo(() => {
    if (!eomInsight) return [];

    // Normalise whitespace
    const raw = eomInsight.replace(/\s+/g, ' ').trim();

    // Support both curly and straight apostrophes
    const pattern = /(Executive Summary:|Current Month Analysis:|Historic Trend Insight:|Forward-Looking Projection Commentary:)/g;

    const parts = raw.split(pattern).filter(Boolean);
    const sections = [];

    for (let i = 0; i < parts.length; i += 2) {
      const label = parts[i];
      const text = (parts[i + 1] || '').trim();
      if (!label || !text) continue;
      sections.push({ label, text });
    }
    return sections;
  }, [eomInsight]);
  
    // Parse RxAI expected orders summary into labelled sections
  const parsedExpectedSections = React.useMemo(() => {
    if (!expectedInsight) return [];

    const raw = expectedInsight.replace(/\s+/g, ' ').trim();

    // Support both curly and straight apostrophes
    const pattern = /(Today[’']s position:|Recent pace:|Drivers:|What to watch next:)/g;

    const parts = raw.split(pattern).filter(Boolean);
    const sections = [];

    for (let i = 0; i < parts.length; i += 2) {
      const label = parts[i];
      const text = (parts[i + 1] || '').trim();
      if (!label || !text) continue;
      sections.push({ label, text });
    }
    return sections;
  }, [expectedInsight]);
  
  // Refs for export
  const chartRefMain = useRef(null);
  const chartRefYTD = useRef(null);
  const chartRefExpected = useRef(null);
  const chartRefInsights = useRef(null);
  // Also add wrapper refs for full-section exports
  const expectedSectionRef = useRef(null);

  const WHOLESALER_OPTIONS = [
    { value: 'ALL', label: 'All wholesalers' },
    { value: 'AAH', label: 'AAH' },
    { value: 'ALLIANCE', label: 'Alliance' },
    { value: 'PHOENIX', label: 'Phoenix' },
    { value: 'ETHIGEN', label: 'Ethigen' },
    { value: 'PRINWEST', label: 'Prinwest' },
  ];
  const PRODUCT_OPTIONS = [
    { value: 'ALL', label: 'All products' },
    { value: 'C250', label: 'C250' },
    { value: 'C500', label: 'C500' },
    { value: 'SimAlvia', label: 'SimAlvia' },
    { value: 'Transisoft', label: 'Transisoft' },
  ];
  
  const [selWholesaler, setSelWholesaler] = useState('ALL');
  const [selProduct, setSelProduct] = useState('ALL');
  const [selValue, setSelValue] = useState('cash');
  const [valueOptions, setValueOptions] = useState([{ value: 'cash', label: 'Cash' },{ value: 'units', label: 'Units' }]);

  
  useEffect(() => {
    const wholesaler = selWholesaler;
    const product = selProduct;
    let val = selValue;

    let key = 'portfolio_cash';

    if (product === 'ALL') {
      val = 'cash';
      if (wholesaler === 'ALL') {
        key = `portfolio_${val}`;
      } else {
        key = `wh_${wholesaler}_${val}_portfolio`;
      }
    } else {
      if (wholesaler === 'ALL') {
        key = `product_${val}_${product}`;
      } else {
        key = `wh_${wholesaler}_${val}_${product}`;
      }
    }

    if (product === 'ALL') {
      setValueOptions([{ value: 'cash', label: 'Cash' }]);
    } 
    else {
      setValueOptions([{ value: 'cash', label: 'Cash' },{ value: 'units', label: 'Units' }]);
    }
    // if we had to change v, reflect it in UI silently
    if (val !== selValue) setSelValue(val);
    setSeriesKey(key);
  }, [selWholesaler, selProduct, selValue]);

  const monthName = React.useMemo(() => {
    const d = asOf ? new Date(asOf + 'T00:00:00') : new Date();
    return d.toLocaleString('en-GB', { month: 'long' });
  }, [asOf]);

  const buildChartContext = React.useCallback(() => {
    const lastPoints = (data || []).slice(-20).map(p => ({ day: p.day, current: p.current }));
    const ytdTail = (ytdData || []).slice(-20).map(p => ({ date: p.date, ytd: p.ytd, eoy: p.eoy_forecast }));
    return {
      seriesKey, title, metric, asOf, monthName,
      ytd: { asOf: ytdAsOf, totals: ytdTotals },
      samples: { month: lastPoints, ytd: ytdTail }
    };
  }, [seriesKey, title, metric, asOf, monthName, ytdAsOf, ytdTotals, data, ytdData]);

  const LABELS = [
    `Cumulative Daily Sales (${monthName})`,
    'Average last 2 months sales',
    'Average last 3 months sales',
    'Average last 6 months sales',
    'EOM Forecast predicted by RxAI',
    `${monthName} Budget`
  ];

  // Ensure the ExFac thread exists
  const ensureThread = React.useCallback(async () => {
    if (aiThreadId) return aiThreadId;
    const res = await fetch(`${apiUrl}/api/start_new_chat_exfac`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    const json = await res.json();
    setAiThreadId(json.thread_id);
    return json.thread_id;
  }, [apiUrl, aiThreadId]);

  // Send a question to backend, prefixing with compact page context
  const sendAi = React.useCallback(async (question) => {
    const threadId = await ensureThread();
    const ctx = buildChartContext();
    const composed = `Context: ${JSON.stringify(ctx)}\n\nQuestion: ${question}`;

    setAiMsgs(m => [...m, { role: 'user', content: question }]);
    setAiLoading(true); setAiError('');

    try {
      const res = await fetch(`${apiUrl}/api/ask_ex_fac`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question: composed, thread_id: threadId })
      });
      const json = await res.json();
      if (json.answer?.headers && json.answer?.rows) {
        const tableText = [json.answer.headers.join(' | '), ...json.answer.rows.map(r => r.join(' | '))].join('\n');
        setAiMsgs(m => [...m, { role: 'assistant', content: tableText }]);
      } else {
        setAiMsgs(m => [...m, { role: 'assistant', content: (typeof json.answer === 'string' ? json.answer : JSON.stringify(json.answer)) }]);
      }
    } catch (e) {
      setAiError(String(e.message || e));
    } finally {
      setAiLoading(false);
    }
  }, [apiUrl, ensureThread, buildChartContext]);

  // ---------------- Fetch ----------------
  const fetchLines = React.useCallback(async () => {
    if (selectedDate == null) return;

    setLoading(true); setError('');
    try {
      const res = await fetch(`${apiUrl}/api/cumulative/lines`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seriesKey, use_business_days: true, selectedDate: selectedDate })
      });
      const json = await res.json();
      setEomChartPayload(json);   // <--- add this
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setAsOf(selectedDate || '');
      setTitle(json.monthly_forecast.title || 'Cumulative Daily');
      setActualSales(json.monthly_forecast.meta.actual_sales || 0);
      setMetric(json.monthly_forecast.metric || 'value');
      setEOMForecast(json.monthly_forecast.meta.forecast.projected_month_total)
      const parsed = Array.isArray(json.monthly_forecast.points) ? json.monthly_forecast.points.map(p => {
        const day = parseInt((p.date || '').split('-')[2] || '0', 10);
        return { day, current: p.current, avg2: p.avg2, avg3: p.avg3, avg6: p.avg6, date: p.date, eom_forecast: p.eom_forecast, budget_flat: p.budget_flat};
      }) : [];
      setData(parsed);
      setExpectedRemaining(json.monthly_forecast.meta.forecast.wholesale_remaining || {});
      const metaObject = {
        fraction_elapsed: json.monthly_forecast.meta.fraction_elapsed,
        month_end: json.monthly_forecast.meta.month_end,
        month_start: json.monthly_forecast.meta.month_start,
        remaining_days: json.monthly_forecast.meta.remaining_days,
       }
      setExpMeta(metaObject);
    } catch (e) {
      setError(String(e.message || e));
      setData([]);
    } finally { setLoading(false); }
  }, [apiUrl, seriesKey, selectedDate]);

  // fetch YTD+EOY series for same seriesKey
  const fetchYTD = React.useCallback(async () => {
    if (selectedDate == null) return;
    setYtdLoading(true); setYtdError('');
    try {
      const res = await fetch(`${apiUrl}/api/cumulative/ytd_forecast`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seriesKey, use_business_days: true, selectedDate: selectedDate })
      });
      const json = await res.json();
      // setExpectedOrdersPayload(json);
      if (!res.ok) throw new Error(json.error || 'Failed to load YTD/EOY');

      setYtdAsOf(selectedDate);
      setYtdMetric(json.ytd_forecast.metric || 'value');
      setYtdTotals({
        ytd_total: Number(json.ytd_forecast.ytd_total || 0),
        eoy_total_forecast: Number(json.ytd_forecast.eoy_total_forecast ?? json.ytd_forecast.eoy_total ?? 0),
      });

      const rows = Array.isArray(json.ytd_forecast.points) ? json.ytd_forecast.points.map(p => ({
        date: p.date, ytd: p.ytd, eoy_forecast: p.eoy_forecast
      })) : [];
      setYtdData(rows);
      setMonthlyBudgets(Array.isArray(json.ytd_forecast.monthly_budgets?.monthly) ? json.ytd_forecast.monthly_budgets.monthly : []);
      setFYBudget(json.ytd_forecast.monthly_budgets.annual_total);
    } catch (e) {
      setYtdError(String(e.message || e));
      setYtdData([]);
    } finally { setYtdLoading(false); }
  }, [apiUrl, seriesKey, selectedDate]);

  const handleEomInsight = async () => {
    setEomInsight('');
    setEomInsightError('');
    setEomWatchTargets([]);
    if (!dayjs(selectedDate).isSame(dayjs(), 'day')) return;
    setEomInsightLoading(true);

    try {
      const res = await fetch(`${apiUrl}/api/exfac/graph_insight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          seriesKey,
          insight_type: 'eom',
        }),
      });

      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || 'Failed to generate insight.');
      }
      let insightText = '';
      let suggestions = [];

      // Case 1: Normal correct response
      if (typeof json.insight === 'string') {
        insightText = json.insight;
      }
      // Case 2: Backend accidentally returned object in .insight
      else if (json.insight && typeof json.insight === 'object') {
        insightText = json.insight.summary_text || JSON.stringify(json.insight);
      }

      // Extract suggestions – prefer the dedicated field
      if (Array.isArray(json.monitoring_suggestions)) {
        suggestions = json.monitoring_suggestions;
      }
      // Fallback: maybe they are inside the object that came in .insight
      else if (json.insight && Array.isArray(json.insight.monitoring_suggestions)) {
        suggestions = json.insight.monitoring_suggestions;
      }

      setEomInsight(insightText.trim());
      setEomWatchTargets(suggestions);
    } catch (err) {
      setEomInsightError(String(err.message || err));
      setEomInsight('');
      setEomWatchTargets([]);
    } finally {
      setEomInsightLoading(false);
    }
  };

  const checkForPermission = (permission) => {
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    return permissions.includes(permission);
  }; 

  useEffect(() => { fetchLines(); fetchYTD(); }, [fetchLines, fetchYTD]);

  useEffect(() => { handleEomInsight(); }, [selectedDate]);

  const ytdMonthDayCounts = React.useMemo(() => {
    const counts = {};
    for (const p of ytdData || []) {
      if (!p?.date) continue;
      const m = String(p.date).slice(0, 7);
      counts[m] = (counts[m] || 0) + 1;
    }
    return counts;
  }, [ytdData]);

  // TODO: CHECK THIS
  const loadMonitorSnapshot = React.useCallback(async () => {
    setMonitorSnapshotLoading(true);
    setMonitorSnapshotError('');

    try {
      const metricParam = 'value'; // monitoring cash for now

      const res = await fetch(
        `${apiUrl}/api/exfac/monitor_snapshot?seriesKey=${encodeURIComponent(
          seriesKey
        )}&metric=${metricParam}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );
      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || 'Failed to load monitoring snapshot');
      }

      console.log('monitor_snapshot JSON', json);

      // Monitors (cards)
      setMonitorSnapshot(Array.isArray(json.monitors) ? json.monitors : []);

      // Portfolio-level numbers
      setMonitorPortfolio(json.portfolio || null);

      // Overall AI commentary
      let overall = '';
      if (Array.isArray(json.ai_commentary) && json.ai_commentary.length > 0) {
        overall = json.ai_commentary[0] || '';
      } else if (typeof json.ai_commentary === 'string') {
        overall = json.ai_commentary;
      }
      setMonitorPortfolioComment(overall);
    } catch (e) {
      setMonitorSnapshotError(String(e.message || e));
      setMonitorSnapshot([]);
      setMonitorPortfolio(null);
      setMonitorPortfolioComment('');
    } finally {
      setMonitorSnapshotLoading(false);
    }
  }, [apiUrl, seriesKey]);

  // Build a map: date -> cumulative budget up to that date
  const budgetCurveMap = React.useMemo(() => {
    if (!ytdData?.length || !monthlyBudgets?.length) return null;
    const budgetByMonth = monthlyBudgets.reduce((acc, b) => {
      const m = String(b.month).slice(0, 7);          // "YYYY-MM" whether you pass "YYYY-MM" or "YYYY-MM-01"
      const v = Number(b.budget) || 0;
      acc.set(m, (acc.get(m) || 0) + v);              // accumulate
      return acc;
    }, new Map());

    const ytdSorted = [...ytdData].sort((a, b) => String(a.date).localeCompare(String(b.date)));

    let cum = 0;
    const perDayCache = {};
    const map = {};
    for (const p of ytdSorted) {
      const d = p.date; if (!d) continue;
      const m = String(d).slice(0, 7);
      if (!(m in perDayCache)) {
        const monthBudget = budgetByMonth.get(m) || 0;
        const nDays = ytdMonthDayCounts[m] || 1;
        perDayCache[m] = monthBudget / nDays;
      }
      cum += perDayCache[m];
      map[d] = cum;
    }
    return map;
  }, [ytdData, monthlyBudgets, ytdMonthDayCounts]);

  // Merge budget curve into the series used by the chart
  const ytdChartData = React.useMemo(() => {
    if (!ytdData?.length) return [];
    if (!budgetCurveMap) return ytdData;
    return ytdData.map(p => ({ ...p, budget_cum: budgetCurveMap[p.date] ?? null }));
  }, [ytdData, budgetCurveMap]);

  const showRxAIInsight = () => {
    return checkForPermission("view_rx_insight");
  };
  // ---------------- Derived: key numbers ----------------

  const keyStats = React.useMemo(() => {
    if (!data.length) return null;
    const last = data[data.length - 1];
    let latestCur = actualSales;
    return { 
      current: latestCur || 0, 
      avg2: last?.avg2 || 0, 
      avg3: last?.avg3 || 0, 
      avg6: last?.avg6 || 0, 
      eom_forecast: last?.eom_forecast || 0, 
      budget_flat: last?.budget_flat || 0 
    };
  }, [data]);

  const monthlyBudget = React.useMemo(() => {
    if (!ytdAsOf?.length) return 0;
    if (!budgetCurveMap) return 0;
    return budgetCurveMap[ytdAsOf];
  }, [ytdAsOf, budgetCurveMap]);
  
    // Current month budget specifically for Monitoring (not YTD)
  const monitorMonthBudget = React.useMemo(() => {
    if (!monthlyBudgets?.length) return 0;

    // Use asOf (month view) first, then ytdAsOf, then today
    const refDate = asOf || ytdAsOf || new Date().toISOString().slice(0, 10);
    const monthKey = String(refDate).slice(0, 7); // "YYYY-MM"

    const match = monthlyBudgets.find(
      (m) => String(m.month).slice(0, 7) === monthKey
    );

    return Number(match?.budget || 0);
  }, [monthlyBudgets, asOf, ytdAsOf]);

  // Gap vs budget for Monitoring = projected month cash – current month budget
  const monitorGap = React.useMemo(() => {
    if (!monitorPortfolio) return 0;
    const proj = Number(monitorPortfolio.proj_month_cash || 0);
    return proj - monitorMonthBudget;
  }, [monitorPortfolio, monitorMonthBudget]);

  // ---------------- Export helpers ----------------
  const filenameBase = React.useMemo(() => {
    const safeTitle = (title || 'chart').replace(/\s+/g, '_').replace(/[^\w\-_.]+/g, '');
    const safeDate  = (asOf || '').replace(/-/g, '');
    return `${safeTitle}_${safeDate || 'today'}`;
  }, [title, asOf]);

  const chartData = React.useMemo(() => {
    const entries = Object.entries(expectedRemaining || {}).map(([wholesaler, data]) => ({
      wholesaler,
      forecast_total: Number(data?.forecast_total ?? 0),
      mtd: Number(data?.mtd ?? 0),
      remaining: Number(data?.remaining ?? 0),   // <––– main value for chart
    }));
    
    return entries.sort((a, b) => b.remaining - a.remaining);
  }, [expectedRemaining]);

  async function loadImage(dataUrl) {
    return new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = dataUrl;
    });
  }
  async function nodeToPngDataUrl(node, { scale = 2, backgroundColor = '#ffffff' } = {}) {
    // fonts ready = crisp text
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch {}
    }
    return htmlToImage.toPng(node, {
      pixelRatio: scale,
      backgroundColor,
      cacheBust: true,
    });
  }

  // --- Build monitoring stats (wholesaler-level) ---
  // Monitoring: group snapshot into High / Medium / Low risk buckets
    // Group snapshot monitors into High / Medium / Low risk buckets
  const groupedMonitorSnapshot = useMemo(() => {
    if (!monitorSnapshot || monitorSnapshot.length === 0) {
      return { High: [], Medium: [], Low: [] };
    }

    const buckets = { High: [], Medium: [], Low: [] };

    monitorSnapshot.forEach((m) => {
      const share = m.share_of_gap_current ?? 0;

      let riskRaw = (m.risk_level || '').toLowerCase();
      let risk;

      if (riskRaw === 'high' || riskRaw === 'medium' || riskRaw === 'low') {
        risk = riskRaw.charAt(0).toUpperCase() + riskRaw.slice(1);
      } else {
        if (share > 0.4) risk = 'High';
        else if (share > 0.2) risk = 'Medium';
        else risk = 'Low';
      }

      const item = { ...m, _riskLabel: risk };

      if (!buckets[risk]) buckets[risk] = [item];
      else buckets[risk].push(item);
    });

    ['High', 'Medium', 'Low'].forEach((bucketKey) => {
      buckets[bucketKey].sort(
        (a, b) => (b.share_of_gap_current ?? 0) - (a.share_of_gap_current ?? 0)
      );
    });

    return buckets;
  }, [monitorSnapshot]);

    // Headlines from portfolio-level commentary (first 4–5 sentences)
  const monitorHeadlines = useMemo(() => {
    if (!monitorPortfolioComment) return [];
    const sentences = monitorPortfolioComment
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return sentences.slice(0, 5);
  }, [monitorPortfolioComment]);


  // wait for N animation frames
  function rafFrames(n = 2) {
    return new Promise((resolve) => {
      const step = () => (n-- <= 0 ? resolve() : requestAnimationFrame(step));
      requestAnimationFrame(step);
    });
  }

  // wait until element size is stable for a short quiet window
  async function waitForStableSize(node, quietMs = 100, timeoutMs = 1500) {
    if (!node) return;
    const start = performance.now();
    let lastW = 0,
      lastH = 0,
      lastChange = performance.now();

    while (performance.now() - start < timeoutMs) {
      await rafFrames(1);
      const { width, height } = node.getBoundingClientRect();
      if (width !== lastW || height !== lastH) {
        lastW = width;
        lastH = height;
        lastChange = performance.now();
      }
      if (performance.now() - lastChange >= quietMs) return;
    }
  }

  // wait until Recharts has drawn line/bar paths
  async function waitForRechartsDrawn(node, timeoutMs = 1500) {
    if (!node) return;
    const start = performance.now();
    while (performance.now() - start < timeoutMs) {
      await rafFrames(1);
      const svg = node.querySelector('svg');
      if (svg) {
        const hasLines = svg.querySelectorAll('.recharts-line path').length > 0;
        const hasBars =
          svg.querySelectorAll(
            '.recharts-bar-rectangle path, .recharts-bar-rectangle rect'
          ).length > 0;
        if (hasLines || hasBars) return;
      }
    }
  }

  async function handleExportPNG() {
    const EXPORT_WIDTH = 1000;
    const SCALE = 2;
    const PADDING = 24;
    const GAP = 24;
    const base = filenameBase || 'charts';

    // temp styles: hide tooltips & CSV button
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .recharts-tooltip-wrapper, .recharts-default-tooltip, .recharts-crosshair { display: none !important; }
      .no-export { display: none !important; }
      *:hover { pointer-events: none !important; }
    `;
    document.head.appendChild(styleEl);

    const prevExportMode = exportMode;
    setExportMode(true); // disables line animations in your YTDChart

    const nodes = [
      chartRefInsights.current,
      chartRefMain.current,
      chartRefYTD.current,
      chartRefExpected.current,
    ].filter(Boolean);
    const originals = nodes.map((n) => ({ node: n, width: n.style.width }));
    nodes.forEach((n) => {
      n.style.width = `${EXPORT_WIDTH}px`;
    });

    try {
      // Let layout settle & Recharts redraw
      await rafFrames(2);
      window.dispatchEvent(new Event('resize'));
      await waitForStableSize(document.body, 120);
      // Ensure each chart has drawn its paths
      for (const n of nodes) {
        await waitForStableSize(n, 80);
        await waitForRechartsDrawn(n);
      }

      // Capture each section
      const [png1, png2, png3, png4] = await Promise.all([
        nodeToPngDataUrl(chartRefInsights.current, { scale: SCALE }),
        nodeToPngDataUrl(chartRefMain.current, { scale: SCALE }),
        nodeToPngDataUrl(chartRefYTD.current, { scale: SCALE }),
        nodeToPngDataUrl(chartRefExpected.current, { scale: SCALE }),
      ]);

      const [img1, img2, img3, img4] = await Promise.all([
        loadImage(png1),
        loadImage(png2),
        loadImage(png3),
        loadImage(png4),
      ]);

      // Compose
      const pad = Math.round(PADDING * SCALE);
      const gap = Math.round(GAP * SCALE);
      const outW = EXPORT_WIDTH * SCALE + pad * 2;
      const outH =
        pad + img1.height + gap + img2.height + gap + img3.height + gap + img4.height + pad;

      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, outW, outH);

      let top = pad,
        left = pad;
      ctx.drawImage(img1, left, top);
      top += img1.height + gap;
      ctx.drawImage(img2, left, top);
      top += img2.height + gap;
      ctx.drawImage(img3, left, top);
      top += img3.height + gap;
      ctx.drawImage(img4, left, top);

      const combinedUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = combinedUrl;
      a.download = `${base}__ALL.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error(e);
      alert('Combined export failed.');
    } finally {
      originals.forEach(({ node, width }) => {
        node.style.width = width;
      });
      setExportMode(prevExportMode);
      document.head.removeChild(styleEl);
    }
  }

// -------- AI insight handlers -------

  const handleExpectedInsight = async () => {
    if (!expectedOrdersPayload) {
      setExpectedInsightError(
        'Insight is only available once expected orders data has loaded.'
      );
      setExpectedInsight('');
      return;
    }

    setExpectedInsightLoading(true);
    setExpectedInsightError('');
    try {
      const res = await fetch(`${apiUrl}/api/exfac/graph_insight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          seriesKey,
          insight_type: 'expected_orders',
          expected: expectedOrdersPayload,
        }),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || 'Failed to generate insight.');
      }
      setExpectedInsight(json.insight || '');
    } catch (err) {
      setExpectedInsightError(String(err.message || err));
      setExpectedInsight('');
    } finally {
      setExpectedInsightLoading(false);
    }
  };

  // ----- Rx AI Monitoring handlers -----
  const handleAddMonitor = (target) => {
    if (!target) return;
    setMonitors((prev) => {
      // avoid duplicates (same type + code)
      if (prev.some((m) => m.type === target.type && m.code === target.code)) {
        return prev;
      }
      return [
        ...prev,
        {
          ...target,
          startedAt: new Date().toISOString(),
        },
      ];
    });
  };

  const handleRemoveMonitor = (type, code) => {
    setMonitors((prev) =>
      prev.filter((m) => !(m.type === type && m.code === code))
    );
    setMonitorItems((prev) =>
      prev.filter((m) => !(m.type === type && m.code === code))
    );
  };

  // TODO: CHECK THIS
    const handleStartMonitoring = async (target) => {
      console.log("handleStartMonitoring");
    if (!target) return;

    const id = `${target.type}:${target.code || target.label || ''}`;
    setMonitorLoadingIds((ids) => [...ids, id]);

    try {
      // Decide metric for monitoring – mostly cash, but respect units if selected
      const metricType = selValue === 'units' ? 'units' : 'cash';

      const res = await fetch(`${apiUrl}/api/exfac/monitor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entity_type: target.type,      // 'wholesaler' | 'product' | ...
          entity_code: target.code,      // e.g. 'ALLIANCE'
          entity_label: target.label,    // e.g. 'Alliance Healthcare'
          series_key: seriesKey,
          metric_type: metricType,
        }),
      });

      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || 'Failed to start monitoring');
      }

            // keep local "monitors" list for UI if you still want it
      handleAddMonitor(target);

      // Jump straight into the Monitoring tab – this is the "risk engine room"
      setActiveTab('monitoring');

      // Refresh the backend snapshot so the Monitoring tab reflects it
      await loadMonitorSnapshot();
    } catch (err) {
      console.error('Monitor error', err);
    } finally {
      setMonitorLoadingIds((ids) => ids.filter((x) => x !== id));
    }
  };

    const handleOpenRiskDetail = (item) => {
    if (!item) return;
    setRiskDetailItem(item);
    setRiskDetailOpen(true);
  };

  const handleCloseRiskDetail = () => {
    setRiskDetailOpen(false);
    setRiskDetailItem(null);
  };

  const exportExpectedCSV = () => {
    if (!expRows?.length) return;
    const headers = [
      'Wholesaler','Remaining Cash','Remaining Units','Per Day Cash','Per Day Units',
      'MTD Cash','MTD Units','Projected Month Cash','Projected Month Units'
    ];
  const lines = expRows.map(r => ([
      r.wholesaler,
      Math.round(r.remaining_cash || 0),
      Math.round(r.remaining_units || 0),
      Math.round(r.per_day_cash || 0),
      Math.round(r.per_day_units || 0),
      Math.round(r.mtd_cash || 0),
      Math.round(r.mtd_units || 0),
      Math.round(r.proj_month_cash || 0),
      Math.round(r.proj_month_units || 0),
    ].join(',')));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `expected_orders_${seriesKey}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };


  function PinnedBox({ point, metric, fmtGBP, fmtUnits, rows, ...ctx }) {
    const { offset, xAxisMap, chartWidth = 0 } = ctx;
    const xAxis = Object.values(xAxisMap)[0];

    const anchorX = Math.round(xAxis?.scale?.(point.date) ?? 0) + (offset?.left ?? 0);
    const boxWidth = 220;
    const padding = 8;

    let boxX = anchorX + 60;
    const chartLeft = (offset?.left ?? 0);
    const chartRight = (offset?.left ?? 0) + (ctx?.chartWidth ?? 0);

    // If box goes off right edge — flip to left side
    if (boxX + boxWidth + padding > chartRight) {
      boxX = anchorX - 60 - boxWidth;
    }

    // NEW: Correct left edge overflow
    if (boxX < chartLeft + padding) {
      boxX = chartLeft + padding;
    }
    const boxY = (offset?.top ?? 0) + 12;
    const fmt = (v) => (metric === 'value' ? fmtGBP(v) : fmtUnits(v));

    return (
      <g>
        <foreignObject x={boxX} y={boxY} width={boxWidth} height="200">
          <div
            style={{
              background: 'var(--mui-palette-background-paper, #fff)',
              border: '1px solid var(--mui-palette-divider, #e0e0e0)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              borderRadius: 8,
              padding: 10,
              fontFamily: 'inherit',
              fontSize: 12,
              lineHeight: 1.4,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Date: {labelFmt(point.date)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 6 }}>
              {rows.map(r => (
                <Fragment key={r.key}>
                  <span style={{ width: 10, height: 10, borderRadius: 9999, background: r.color, display: 'inline-block', marginTop: 4 }} />
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}> {r.label} </span>
                  <strong>{fmt(r.value)}</strong>
                </Fragment>
              ))}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  }
  function PinnedTooltipBox({ point, metric, fmtGBP, fmtUnits, rows, ...ctx }) {
    const { offset, xAxisMap } = ctx;
    const xAxis = Object.values(xAxisMap)[0];
    const anchorX = Math.round(xAxis?.scale?.(point.day) ?? 0) + (offset?.left ?? 0);

    const boxWidth = 320;
    const boxHeight = 110;
    const padding = 8;

    let boxX = anchorX + 60;
    const chartLeft = (offset?.left ?? 0);
    const chartRight = (offset?.left ?? 0) + (ctx?.chartWidth ?? 0);

    // If box goes off right edge — flip to left side
    if (boxX + boxWidth + padding > chartRight) {
      boxX = anchorX - 60 - boxWidth;
    }

    // NEW: Correct left edge overflow
    if (boxX < chartLeft + padding) {
      boxX = chartLeft + padding;
    }
    const boxY = (offset?.top ?? 0) + 12;
    const fmt = (v) => (metric === 'value' ? fmtGBP(v) : fmtUnits(v));

    return (
      <g>
        <foreignObject x={boxX} y={boxY} width={boxWidth} height="200">
          <div
            style={{
              background: 'var(--mui-palette-background-paper, #fff)',
              border: '1px solid var(--mui-palette-divider, #e0e0e0)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              borderRadius: 8,
              padding: 10,
              fontFamily: 'inherit',
              fontSize: 12,
              lineHeight: 1.4,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Day {ordinal(Number(point.day))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 6 }}>
              {rows.map(r => (
                <Fragment key={r.key}>
                  <span style={{
                    width: 10, height: 10, borderRadius: 9999, background: r.color, display: 'inline-block', marginTop: 4
                  }} />
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.label}
                  </span>
                  <strong>{fmt(r.value)}</strong>
                </Fragment>
              ))}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  }

  function RxAIInsight() {
    return (
      <Box ref={chartRefInsights} sx={{ width: exportMode ? 1000 : '100%' }} padding={2}>
        { showRxAIInsight() && (
          <>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              Rx AI Insights
            </Typography>
            {eomInsightLoading && (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Generating insights...
              </Typography>
            )}
            {eomInsightError && !eomInsightLoading && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {eomInsightError}
              </Alert>
            )}
            {/* Summary text – structured into labelled sections, non-selectable */}
            {eomInsight && !eomInsightLoading && !eomInsightError && (
              <Box
                sx={{
                  mt: 0.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.default',
                  fontSize: 13,
                  lineHeight: 1.5,
                  userSelect: 'none', // prevent copy
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                }}
              >
                <div style={{ whiteSpace: "pre-line" }}>
                  {eomInsight}
                </div>
              </Box>
            )}
          </> 
          )
        }
      </Box>
    );
  }
  // ---------------- Chart 1: Four lines ----------------
  function FourLineChart() {
    const NAME_MAP = {
      budget_flat: `${monthName} Budget`,
      current: `Cumulative Daily Sales (${monthName})`,
      eom_forecast: `Forecast predicted by RxAI`,
      avg2: 'Average last 2 months sales',
      avg3: 'Average last 3 months sales',
      avg6: 'Average last 6 months sales'
    };
    const CHART_HEIGHT = 420;

    const todayMonthEntry = React.useMemo(() => {
      const byDate = data.find(p => p.date === asOf);
      return byDate || data[data.length - 1] || null;
    }, [data, asOf]);

    // Resolve the index for todayMonthEntry (fallback to last point)
    const selectedIndex = useMemo(() => {
      if (!data?.length) return 0;
      if (todayMonthEntry != null) {
        const i = data.findIndex(d => Number(d.day) === Number(todayMonthEntry.day));
        if (i >= 0) return i;
      }
      return data.length - 1;
    }, [data, todayMonthEntry]);

    const selected = data?.[selectedIndex];

    const rows = [
      { key: 'current',       color: LINE_PALETTE[0], label: LABELS[0],     value: keyStats?.current },
      { key: 'budget_flat',   color: BUDGET_COLOR,    label: LABELS[5],     value: selected?.budget_flat },
      { key: 'eom_forecast',  color: FORECAST_COLOR,  label: LABELS[4],     value: eomForecast },
      { key: 'avg2',          color: LINE_PALETTE[1], label: LABELS[1],     value: selected?.avg2 },
      { key: 'avg3',          color: LINE_PALETTE[2], label: LABELS[2],     value: selected?.avg3 },
      { key: 'avg6',          color: LINE_PALETTE[3], label: LABELS[3],     value: selected?.avg6 },
    ].filter(r => !(r.key === 'eom_forecast' && r.value === 0));

    return (
      <Box ref={chartRefMain} sx={{ width: exportMode ? 1000 : '100%' }} padding={2}>
        {keyStats && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 1.5 }}>
            {rows.map((r, i) => (
              <Box
                key={i}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 999,
                  bgcolor: 'background.paper',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
                title={`${r.label}: ${isCash ? fmtGBP(r.value) : fmtUnits(r.value)}`}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color, flex: '0 0 auto' }} />
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                  {r.label}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {isCash ? fmtGBP(r.value) : fmtUnits(r.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 8, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" tickFormatter={(d)=>ordinal(Number(d))} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v)=> isCash ? fmtGBP(v) : fmtUnits(v)} tick={{ fontSize: 12 }} />
            <RTooltip
              formatter={(val, name, props) => {
                const nice = NAME_MAP[props.dataKey] || name;
                return [isCash ? fmtGBP(val) : fmtUnits(val), nice];
              }}
              labelFormatter={(lbl)=> `Day ${ordinal(Number(lbl))}`}
            />
            <Line type="monotone" dataKey="current" stroke={LINE_PALETTE[0]} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="budget_flat" stroke={BUDGET_COLOR} strokeDasharray="6 4" strokeWidth={2.25} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="eom_forecast" stroke={FORECAST_COLOR} strokeDasharray="6 4" strokeWidth={2.25} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="avg2" stroke={LINE_PALETTE[1]} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="avg3" stroke={LINE_PALETTE[2]} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="avg6" stroke={LINE_PALETTE[3]} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            {exportMode && selected && (
              <ReferenceLine
                x={selected.day}
                stroke="#999"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
              />
            )}
            {exportMode && selected && (
              <Customized
                component={
                  <PinnedTooltipBox
                    point={selected}
                    metric={metric}
                    fmtGBP={fmtGBP}
                    fmtUnits={fmtUnits}
                    rows={rows}
                  />
                }
              />
            )}
          </LineChart>
        </ResponsiveContainer>
        <Typography variant="subtitle2" sx={{ fontWeight:500 }}>
          EOM Forecast predicted by RxAI
        </Typography>
      </Box>
    );
  }

  // ---------------- Chart 2: YTD vs EOY forecast ----------------
  function YTDChart() {

    const todayISO = ytdAsOf || '';
    const todayIndex = ytdChartData.findIndex(p => p.date === todayISO);
    const CHART_HEIGHT = 420;
    const selected = todayIndex >= 0 ? ytdChartData[todayIndex] : null;
    
    const rows = [
      { key: 'ytd',          color: YTD_COLOR, label: 'YTD',           value: selected?.ytd },
      { key: 'eoy_forecast', color: FORECAST_COLOR, label: 'EOY Forecast', value: selected?.eoy_total_forecast },
      { key: 'budget_cum',   color: BUDGET_COLOR, label: 'Budget (YTD)', value: selected?.budget_cum },
    ].filter(r => r.value != null);

    return (
      <Box ref={chartRefYTD} sx={{ width: exportMode ? 1000 : '100%' }} padding={2}>
        <Box sx={{ display:'flex', flexWrap:'wrap', gap: 1.25, mb: 1.5 }}>
          {[
            { color: YTD_COLOR, label: 'YTD', value: ytdTotals.ytd_total },
            { color: BUDGET_COLOR, label: 'YTD Budget', value: monthlyBudget },
            { color: FORECAST_COLOR, label: 'EOY Forecast', value: ytdTotals.eoy_total_forecast },
            { color: BUDGET_COLOR, label: 'FY Budget', value: fyBudget },
          ].map((r, i) => (
            <Box
              key={i}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 999,
                bgcolor: 'background.paper',
              }}
              title={`${r.label}: ${ytdMetric === 'value' ? fmtGBP(r.value) : fmtUnits(r.value)}`}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color, flex: '0 0 auto' }} />
              <Typography variant="body2">{r.label}</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight:700 }}>
                {ytdMetric === 'value' ? fmtGBP(r.value) : fmtUnits(r.value)}
              </Typography>
            </Box>
          ))}
        </Box>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={ytdChartData} margin={{ top: 8, right: 12, bottom: 8, left: 12 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tickFormatter={labelFmt} minTickGap={28} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v)=> ytdMetric === 'value' ? fmtGBP(v) : fmtUnits(v)} tick={{ fontSize: 12 }} />
            <RTooltip
              formatter={(val, _name, props) => {
                const key = props?.dataKey;
                const nice =
                  key === 'ytd' ? 'YTD'
                  : key === 'eoy_forecast' ? 'EOY Forecast'
                  : key === 'budget_cum' ? 'Budget (YTD)'
                  : key;
                return [ytdMetric === 'value' ? fmtGBP(val) : fmtUnits(val), nice];
              }}
              labelFormatter={(iso)=> `Date: ${labelFmt(iso)}`}
            />
            <Line type="monotone" dataKey="ytd" name="YTD" stroke={YTD_COLOR} strokeWidth={2.25} dot={false} isAnimationActive={!exportMode} animationDuration={exportMode ? 0 : 400} />
            <Line type="monotone" dataKey="eoy_forecast" name="EOY Forecast" stroke={FORECAST_COLOR} strokeDasharray="6 4" strokeWidth={2.25} dot={false} connectNulls isAnimationActive={!exportMode} animationDuration={exportMode ? 0 : 400} />
            <Line type="monotone" dataKey="budget_cum" name="Budget (YTD)" stroke={BUDGET_COLOR} strokeDasharray="2 2" strokeWidth={2} dot={false} connectNulls isAnimationActive={!exportMode} animationDuration={exportMode ? 0 : 400} />
            {!exportMode && todayIndex >= 0 && (<ReferenceLine x={todayISO} stroke="#9e9e9e" strokeDasharray="3 3" />)}
            {exportMode && selected && (
              <ReferenceLine
                x={selected.date}
                stroke="#999"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
              />
            )}
            {exportMode && selected && (
              <Customized
                component={
                  <PinnedBox
                    point={selected}
                    metric={ytdMetric}
                    fmtGBP={fmtGBP}
                    fmtUnits={fmtUnits}
                    rows={rows}
                  />
                }
              />
            )}
          </LineChart>
        </ResponsiveContainer>
        <Typography variant="subtitle2" sx={{ fontWeight:500 }}>
          EOY Forecast predicted by RxAI
        </Typography>
      </Box>
    );
  }

  // ---------------- Expected Orders: horizontal bar chart ----------------
  function ExpectedOrdersChart() {
    // Which metric to plot
    const keyName = 'value';
    const fmtShort = isCash ? fmtGBPShort : fmtUnitsShort;
    const fmtFull  = isCash ? fmtGBP      : fmtUnits;
    
    const rows = chartData.map(r => ({
      wholesaler: r.wholesaler,
      value: Number(r.remaining || 0),
      color: whColor(r.wholesaler)
    }));

    // Dynamic height: one row ≈ 40px, bounded for sanity
    const chartHeight = Math.max(260, Math.min(560, rows.length * 42));

    // Minimal tooltip
    const CleanTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const p = payload[0];
      return (
        <div style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: '8px 10px',
          boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
          fontSize: 12
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
          <div>{fmtFull(p.value)}</div>
        </div>
      );
    };

    return (
      <Box ref={chartRefExpected} sx={{ width: exportMode ? 1000 : '100%' }} padding={2}>
        <Box sx={{ display:'flex', flexWrap:'wrap', gap: 1, mb: 1.25 }}>
          <Chip
            size="small"
            label={`Business days left: ${expMeta?.month?.remaining_days ?? expMeta?.remaining_days ?? '—'}`}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`Month progress: ${Math.round(100*(expMeta?.month?.fraction_elapsed ?? expMeta?.fraction_elapsed ?? 0))}%`}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`Scope: ${seriesKey}`}
            variant="outlined"
          />
        </Box>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1 }} ref={expectedSectionRef}>
          <Typography variant="subtitle1" sx={{ fontWeight:700 }}>
            Expected new orders to month end (per wholesaler)
          </Typography>

          <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
            <Button
              className="no-export" 
              startIcon={<DownloadIcon />}
              size="small"
              variant="text"
              onClick={exportExpectedCSV}
              disabled={!expRows?.length}
              sx={{ textTransform:'none', fontWeight:600 }}
              title={expRows?.length ? 'Download expected orders as CSV' : 'No data to export'}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 6, right: 50, bottom: 6, left: 12 }}
              barCategoryGap={12}
              barGap={6}
            >
              <CartesianGrid stroke="#eef2f7" horizontal vertical={false} />
              <XAxis
                type="number"
                tickFormatter={(v) => isCash
                  ? `£${Math.round(v).toLocaleString('en-GB')}`
                  : Math.round(v).toLocaleString('en-GB')}
                tick={{ fontSize: 12, fill: '#475569' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="wholesaler"
                width={110}
                tick={{ fontSize: 8, fill: '#334155', fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
              />
              <RTooltip content={<CleanTooltip />} />
              <Bar
                dataKey="value"
                isAnimationActive={false}
                barSize={20}
                radius={[4, 4, 4, 4]}
              >
                {rows.map((row, i) => (
                  <Cell
                    key={`cell-${i}`}
                    fill={row.color}
                    stroke="#0f172a"
                    strokeOpacity={0.05}
                  />
                ))}
                {/* Value labels at end of bars, compact format */}
                <LabelList
                  dataKey="value"
                  position="right"
                  formatter={(v) => fmtShort(v)}
                  style={{ fontSize: 12, fill: '#334155', fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight:500 }}>
          Expected new wholesaler orders predicted by RxAI
        </Typography>
      </Box>
    );
  }
  
  useImperativeHandle(ref, () => ({
    async getExportPayload() {
      const EXPORT_WIDTH = 1000;
      const SCALE = 2;

      // 1) Hide tooltips + Export CSV during capture
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        .recharts-tooltip-wrapper, .recharts-default-tooltip, .recharts-crosshair { display: none !important; }
        .no-export { display: none !important; }
        *:hover { pointer-events: none !important; }
      `;
      document.head.appendChild(styleEl);

      // 2) disable animations
      const prevExportMode = exportMode;
      setExportMode(true);

      // 3) Lock consistent width for each section
      const nodes = [chartRefInsights.current, chartRefMain.current, chartRefYTD.current, chartRefExpected.current].filter(Boolean);
      const originals = nodes.map(n => ({ node: n, width: n.style.width }));
      nodes.forEach(n => { n.style.width = `${EXPORT_WIDTH}px`; });

      try {
        // 4) Wait for layout + drawing to settle
        await rafFrames(2);
        window.dispatchEvent(new Event('resize'));
        await waitForStableSize(document.body, 120);
        for (const n of nodes) {
          await waitForStableSize(n, 80);
          await waitForRechartsDrawn(n);
        }

        // 5) Rasterize each section to PNG (same scale)
        const [png1, png2, png3, png4] = await Promise.all([
          nodeToPngDataUrl(chartRefInsights.current, { scale: SCALE }),
          nodeToPngDataUrl(chartRefMain.current, { scale: SCALE }),
          nodeToPngDataUrl(chartRefYTD.current,  { scale: SCALE }),
          nodeToPngDataUrl(chartRefExpected.current, { scale: SCALE }),
        ]);

        await Promise.all([
          loadImage(png1), loadImage(png2), loadImage(png3), loadImage(png4)
        ]);

        const chartInsightsPng = png1;
        const chartMainPng = png2;
        const chartYTDPng = png3;
        const chartExpectedPng = png4;

        return {
          chartInsightsPng,
          chartMainPng,
          chartYTDPng,
          chartExpectedPng
        };
      } finally {
        // 7) Restore UI
        originals.forEach(({ node, width }) => { node.style.width = width; });
        setExportMode(prevExportMode);
        document.head.removeChild(styleEl);
      }
    }
  }));

  // Monitoring: effective month budget and gap vs budget
  // We want the *current month* budget (not FY) and a gap that is:
  //   gap = projected month (EOM) – month budget
  //   > 0  → ahead of budget
  //   < 0  → behind budget
  const effectiveMonthBudget = React.useMemo(() => {
    const backendMonth = Number(monitorPortfolio?.month_budget || 0);
    if (backendMonth > 0) return backendMonth;

    const dashMonth = Number(monthlyBudget || 0);
    if (dashMonth > 0) return dashMonth;

    return 0;
  }, [monitorPortfolio, monthlyBudget]);

  const projMonth = React.useMemo(
    () => Number(monitorPortfolio?.proj_month_cash || 0),
    [monitorPortfolio]
  );

  // Monitoring: gap vs budget using current month budget only
const gapVsBudget = React.useMemo(() => {
  if (!monitorPortfolio) return null;
  const budget = monitorMonthBudget;
  if (!budget) return null;

  const proj = Number(monitorPortfolio.proj_month_cash || 0);
  return proj - budget;   // +ve = ahead of budget, -ve = behind
}, [monitorPortfolio, monitorMonthBudget]);

// Headline sentence for "Today's Rx AI headlines" – use the same gap
const portfolioGapHeadline = React.useMemo(() => {
  if (gapVsBudget === null) return '';

  if (gapVsBudget === 0) {
    return 'The portfolio is currently on budget.';
  }

  const prettyGap =
    '£' + Math.abs(Math.round(gapVsBudget)).toLocaleString('en-GB');

  return gapVsBudget > 0
    ? `The portfolio is currently ${prettyGap} ahead of budget.`
    : `The portfolio is currently ${prettyGap} behind budget.`;
}, [gapVsBudget]);

  const ragCounts = React.useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    if (!monitorSnapshot?.length) return counts;

    monitorSnapshot.forEach((m) => {
      const share = m.share_of_gap_current ?? 0;
      let risk = (m.risk_level || '').toLowerCase();

      if (risk !== 'high' && risk !== 'medium' && risk !== 'low') {
        if (share > 0.4) risk = 'high';
        else if (share > 0.2) risk = 'medium';
        else risk = 'low';
      }

      if (risk === 'high') counts.high += 1;
      else if (risk === 'medium') counts.medium += 1;
      else counts.low += 1;
    });

    return counts;
  }, [monitorSnapshot]);

  const topHighRisk = React.useMemo(() => {
    if (!monitorSnapshot?.length) return null;
    const highItems = monitorSnapshot
      .filter((m) => {
        const share = m.share_of_gap_current ?? 0;
        let risk = (m.risk_level || '').toLowerCase();
        if (risk !== 'high') {
          if (share > 0.4) risk = 'high';
        }
        return risk === 'high';
      })
      .sort(
        (a, b) =>
          (b.share_of_gap_current ?? 0) - (a.share_of_gap_current ?? 0)
      );
    return highItems[0] || null;
  }, [monitorSnapshot]);

    // Snappy explanation text for the risk detail dialog
  const riskDetailText = React.useMemo(() => {
    if (!riskDetailItem) return '';

    const name =
      riskDetailItem.entity_label ||
      riskDetailItem.entity_code ||
      'this wholesaler';

    const rawRisk =
      (riskDetailItem._riskLabel || riskDetailItem.risk_level || '')
        .toString()
        .toLowerCase();

    const riskLabel =
      rawRisk === 'high' || rawRisk === 'medium' || rawRisk === 'low'
        ? rawRisk
        : 'low';

    const sharePct = Math.round(
      (riskDetailItem.share_of_gap_current ?? 0) * 100
    );

    const remainingVal = Number(riskDetailItem.remaining_current ?? 0);
    const remainingText = isCash
      ? fmtGBP(remainingVal)
      : fmtUnits(remainingVal);

    const sentences = [];

    // 1) Risk + share of gap
    sentences.push(
      `${name} is currently flagged as ${riskLabel} risk, carrying around ${sharePct}% of the remaining gap this month.`
    );

    // 2) Expected remaining this month
    if (remainingVal > 0) {
      sentences.push(
        `RxAI expects roughly ${remainingText} still to land from ${name} before month end.`
      );
    }

    // 3) Brief monitoring guidance
    if (riskLabel === 'high') {
      sentences.push(
        'This wholesaler is a key driver of whether the portfolio closes the month closer to budget and should be watched closely for late-month orders.'
      );
    } else if (riskLabel === 'medium') {
      sentences.push(
        'Movements here can meaningfully shift the month-end outcome and should be checked regularly alongside other high-risk items.'
      );
    } else {
      sentences.push(
        'This item is being tracked for completeness but is not a primary driver of the overall gap on its own.'
      );
    }

    return sentences.join(' ');
  }, [riskDetailItem, isCash, fmtGBP, fmtUnits]);

  const headlineInsight = React.useMemo(() => {
    if (!monitorPortfolioComment) return '';
    // First sentence only for the headline; full text stays in the body
    const firstSentence = monitorPortfolioComment
      .split(/(?<=[.!?])\s+/)[0]
      .trim();
    return firstSentence;
  }, [monitorPortfolioComment]);

  // ---------------- Render ----------------
    return (
    <Box sx={{ padding: 1, minHeight: '100vh' }}>
      <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 1.5 }}>
          <FormControl size="small" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <InputLabel id="wholesaler-label">Wholesaler</InputLabel>
            <Select labelId="wholesaler-label" label="Wholesaler" value={selWholesaler} onChange={(e)=>setSelWholesaler(e.target.value)}>
              {WHOLESALER_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <InputLabel id="product-label">Product</InputLabel>
            <Select labelId="product-label" label="Product" value={selProduct} onChange={(e)=>setSelProduct(e.target.value)}>
              {PRODUCT_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <InputLabel id="value-label">Value</InputLabel>
            <Select labelId="value-label" label="Value" value={selValue} onChange={(e)=>setSelValue(e.target.value)}>
              {valueOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Tabs header */}
      {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Ex-Factory Performance
        </Typography>

        <ToggleButtonGroup
          size="small"
          value={activeTab}
          exclusive
          onChange={(_, v) => v && setActiveTab(v)}
        >
          <ToggleButton value="dashboard">Dashboard</ToggleButton>
          <ToggleButton value="monitoring">Rx AI Monitoring</ToggleButton>
        </ToggleButtonGroup>
      </Box> */}

      {/* DASHBOARD TAB */}
      {activeTab === 'dashboard' && (
        <Grid container spacing={1} alignItems="stretch">
          <Grid item xs={12}>
            <SectionCard
              title={title || 'Cumulative Daily'}
              subtitle={isCash ? '£ Cash (cumulative)' : 'Units (cumulative)'}
              right={
                <Box sx={{ display:'flex', gap: 1, flexWrap:'wrap' }}>
                  {showAskRxAI && 
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onAskRxAI()}
                      sx={{ borderRadius: 999, textTransform:'none', fontWeight:600 }}
                    >
                      Ask about this
                    </Button>
                  }

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportPNG}
                    sx={{ borderRadius: 999, textTransform:'none', fontWeight:600 }}
                  >
                    Export PNG
                  </Button>
                </Box>
              }
            >
              {loading && <CircularProgress size={22} />}
              {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              {!loading && !error && (
                <>
                  {/* Rx AI Summary panel */}
                  <RxAIInsight />
                  
                  {/* Chart #1 */}
                  <FourLineChart />
                  {ytdLoading && <CircularProgress size={22} />}
                  {!ytdLoading && ytdError && <Alert severity="error" sx={{ mb: 2 }}>{ytdError}</Alert>}
                  {!ytdLoading && !ytdError && <YTDChart />}

                  {/* ===== EXPECTED ORDERS BLOCK ===== */}
                  <Box sx={{ mt: 3 }}>
                    {(expLoading) && <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}><CircularProgress size={20}/><Typography>Computing expected new orders…</Typography></Box>}
                    {expError && !expLoading && (
                      <Alert severity="info" sx={{ mb: 2 }}>{expError}</Alert>
                    )}

                    {!expLoading && (
                      <>
                        <ExpectedOrdersChart />

                        {/* Expected Orders Insight – structured sections */}
                        {/* <Box sx={{ mt: 1.5, mb: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleExpectedInsight}
                            disabled={expectedInsightLoading}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
                          >
                            {expectedInsightLoading ? 'Generating insight…' : 'Explain these expected orders'}
                          </Button>

                          {expectedInsightError && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {expectedInsightError}
                            </Alert>
                          )}

                          {expectedInsight && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                RxAI – Expected Orders Insight
                              </Typography>

                              <Box
                                sx={{
                                  mt: 0.5,
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  backgroundColor: 'background.default',
                                  fontSize: 13,
                                  lineHeight: 1.5,
                                  userSelect: 'none',
                                  WebkitUserSelect: 'none',
                                  MozUserSelect: 'none',
                                }}
                              >
                                {parsedExpectedSections.length > 0 ? (
                                  parsedExpectedSections.map((sec, idx) => (
                                    <Box key={idx} sx={{ mb: 1 }}>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 700, mb: 0.25 }}
                                      >
                                        {sec.label.replace(':', '')}
                                      </Typography>
                                      <Typography variant="body2">
                                        {sec.text}
                                      </Typography>
                                    </Box>
                                  ))
                                ) : (
                                  <Typography variant="body2">
                                    {expectedInsight}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box> */}
                      </>
                    )}
                  </Box>

                  {/* Floating Ask button */}
                  {showAskRxAI && !aiOpen && (
                    <Box sx={{ position:'fixed', right: 24, bottom: 24, zIndex: 1300 }}>
                      <Button
                        variant="contained"
                        onClick={() => onAskRxAI()}
                        sx={{ borderRadius: 999, px: 2.25, py: 1, boxShadow: 4, textTransform:'none', fontWeight:700 }}
                      >
                        Ask RxAI
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {/* MONITORING TAB */}
      {activeTab === 'monitoring' && (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <SectionCard
              title="Rx AI Monitoring"
              subtitle="Watchlist of wholesalers and products driving this month’s result"
            >
              {/* ===== Rx AI Monitoring (backend snapshot) ===== */}
              <Box sx={{ mt: 1 }}>

                          {/* Today's Rx AI headlines */}
                {monitorSnapshot.length > 0 && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.75,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 0.75 }}
                    >
                      Today&apos;s Rx AI headlines
                    </Typography>

                    <Box
                      component="ul"
                      sx={{ pl: 2.5, m: 0, fontSize: 13, lineHeight: 1.6 }}
                    >
                      {portfolioGapHeadline && <li>{portfolioGapHeadline}</li>}

                      <li>
                        High risk: {ragCounts.high} key driver
                        {ragCounts.high === 1 ? '' : 's'} require close attention.
                      </li>

                      <li>
                        Medium risk: {ragCounts.medium} item
                        {ragCounts.medium === 1 ? '' : 's'} with meaningful impact
                        this month.
                      </li>

                      <li>
                        Low risk: {ragCounts.low} item
                        {ragCounts.low === 1 ? '' : 's'} being tracked for
                        completeness.
                      </li>

                      {topHighRisk && (
                        <li>
                          Key driver: {topHighRisk.entity_label} currently
                          contributes{' '}
                          {Math.round(
                            (topHighRisk.share_of_gap_current ?? 0) * 100
                          )}
                          % of the remaining gap.
                        </li>
                      )}

                      {headlineInsight && (
                        <li>AI insight: {headlineInsight}</li>
                      )}
                    </Box>
                  </Box>
                )}

                        {/* Risk detail overlay */}
              <Dialog
                open={riskDetailOpen}
                onClose={handleCloseRiskDetail}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>
                  {riskDetailItem
                    ? `Risk explanation – ${riskDetailItem.entity_label}`
                    : "Risk explanation"}
                </DialogTitle>
                <DialogContent dividers>
                  {riskDetailItem && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {/* High-level tags */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip
                          size="small"
                          label={`${(riskDetailItem._riskLabel || riskDetailItem.risk_level || "")
                            .toString()
                            .toUpperCase()} risk`}
                          color={
                            (riskDetailItem._riskLabel || riskDetailItem.risk_level) === "High"
                              ? "error"
                              : (riskDetailItem._riskLabel || riskDetailItem.risk_level) ===
                                "Medium"
                              ? "warning"
                              : "success"
                          }
                        />
                        <Chip
                          size="small"
                          label={`Expected remaining: ${
                            isCash
                              ? fmtGBP(riskDetailItem.remaining_current ?? 0)
                              : fmtUnits(riskDetailItem.remaining_current ?? 0)
                          }`}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={`Share of gap: ${Math.round(
                            (riskDetailItem.share_of_gap_current ?? 0) * 100
                          )}%`}
                          variant="outlined"
                        />
                      </Box>

                      {/* Full AI explanation */}
                                      <Typography
                        variant="body2"
                        sx={{ fontSize: 13, lineHeight: 1.6 }}
                      >
                        {riskDetailText}
                      </Typography>
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseRiskDetail}>Close</Button>
                </DialogActions>
              </Dialog>

                {/* Header + Refresh */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Current watchlist
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {monitorSnapshotLoading && (
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Updating…
                      </Typography>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={loadMonitorSnapshot}
                      disabled={monitorSnapshotLoading}
                      sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 600 }}
                    >
                      Refresh monitoring
                    </Button>
                  </Box>
                </Box>

                                                        {/* Portfolio-level summary + AI monitoring headlines */}
                          {monitorPortfolio && (
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                      
      <Chip
              size="small"
              label={`Projected month (EOM): ${fmtGBP(
                monitorPortfolio.proj_month_cash || 0
              )}`}
              variant="outlined"
            />

            {/* Month budget – from monitorMonthBudget (monthly only) */}
            {monitorMonthBudget > 0 && (
              <Chip
                size="small"
                label={`Month budget: ${fmtGBP(monitorMonthBudget)}`}
                variant="outlined"
              />
            )}

            {/* Gap vs budget – based on the same monthly numbers */}
            {gapVsBudget !== null && monitorMonthBudget > 0 && (
              <Chip
                size="small"
                label={`Gap vs budget: ${fmtGBP(
                  Math.abs(gapVsBudget)
                )} ${
                  gapVsBudget > 0
                    ? 'ahead of budget'
                    : gapVsBudget < 0
                    ? 'behind budget'
                    : 'on budget'
                }`}
                variant="outlined"
                color={
                  gapVsBudget > 0
                    ? 'success' // ahead of budget
                    : gapVsBudget < 0
                    ? 'error'   // behind budget
                    : 'default'
                }
              />
            )}

            {/* MTD cumulative sales from the main chart */}
            <Chip
              size="small"
              label={`MTD cumulative sales: ${fmtGBP(keyStats?.current || 0)}`}
              variant="outlined"
            />
                    </Box>

                    {/* REMOVE the second "Today's RxAI monitoring headlines" block completely */}
                    {/* (we keep only the top "Today's Rx AI headlines" section above) */}
                  </Box>
                )}
                      
                
                {/* Errors */}
                {monitorSnapshotError && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    {monitorSnapshotError}
                  </Alert>
                )}

                {/* Empty state */}
                {!monitorSnapshotLoading &&
                  monitorSnapshot.length === 0 &&
                  !monitorSnapshotError && (
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.7, fontStyle: 'italic', userSelect: 'none' }}
                    >
                      No active monitoring yet. Use <strong>Rx AI Summary</strong> to
                      select items for ongoing tracking.
                    </Typography>
                  )}

                                    {/* Snapshots – traffic-light risk engine room */}
                {monitorSnapshot.length > 0 && (
                  <Box>
                    {/* RAG counts header */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Chip
                        size="small"
                        label={`High risk: ${groupedMonitorSnapshot.High.length}`}
                        color="error"
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        size="small"
                        label={`Medium risk: ${groupedMonitorSnapshot.Medium.length}`}
                        color="warning"
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        size="small"
                        label={`Low risk: ${groupedMonitorSnapshot.Low.length}`}
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>

                    {['High', 'Medium', 'Low'].map((bucketKey) => {
                      const bucket = groupedMonitorSnapshot[bucketKey] || [];
                      if (!bucket.length) return null;

                      const isOpen = openBuckets[bucketKey];

                      return (
                        <Box key={bucketKey} sx={{ mb: 1.75 }}>
                          {/* Bucket header – click to expand/collapse */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              mb: isOpen ? 0.75 : 0,
                              px: 0.25,
                            }}
                            onClick={() =>
                              setOpenBuckets((prev) => ({
                                ...prev,
                                [bucketKey]: !prev[bucketKey],
                              }))
                            }
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {bucketKey} risk ({bucket.length})
                            </Typography>
                            <Typography
                              component="span"
                              sx={{ fontSize: 12, opacity: 0.7 }}
                            >
                              {isOpen ? 'Hide' : 'Show'}
                            </Typography>
                          </Box>

                          {isOpen && (
                            <Grid container spacing={1.5}>
                              {bucket.map((m) => {
                                const share = m.share_of_gap_current ?? 0;
                                const remaining = m.remaining_current ?? 0;
                                const deltaProj = m.delta_projection ?? 0;
                                const risk = m._riskLabel || bucketKey;

                                return (
                                  <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    key={m.id || `${m.entity_type}_${m.entity_code}`}
                                  >
                                    <Card
                                      sx={{
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: '0 4px 10px rgba(15,23,42,0.04)',
                                        userSelect: 'none',
                                      }}
                                    >
                                      <CardContent sx={{ p: 1.75 }}>
                                        {/* Header */}
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            justifyContent: 'space-between',
                                            mb: 0.75,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 700 }}
                                          >
                                            {m.entity_label}
                                          </Typography>

                                          <Chip
                                            size="small"
                                            label={`${risk} risk`}
                                            color={
                                              risk === 'High'
                                                ? 'error'
                                                : risk === 'Medium'
                                                ? 'warning'
                                                : 'success'
                                            }
                                            sx={{
                                              fontWeight: 600,
                                              textTransform: 'none',
                                            }}
                                          />
                                        </Box>

                                                              {/* AI commentary – short teaser only */}
                            {m.ai_comment && (
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontSize: 13, lineHeight: 1.5 }}
                              >
                                {m.ai_comment.length > 200
                                  ? `${m.ai_comment.slice(0, 200)}…`
                                  : m.ai_comment}
                              </Typography>
                            )}

                            {/* Core stats only – keep this simple */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                gap: 1,
                                fontSize: 12,
                              }}
                            >
                              <Box>
                                <Typography sx={{ opacity: 0.7 }}>
                                  Expected remaining this month
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {isCash ? fmtGBP(remaining) : fmtUnits(remaining)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography sx={{ opacity: 0.7 }}>
                                  Share of gap
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {Math.round((share || 0) * 100)}%
                                </Typography>
                              </Box>
                            </Box>

                                                  {/* Actions */}
                            <Box
                              sx={{
                                mt: 1,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Button
                                size="small"
                                sx={{ textTransform: "none", fontSize: 12 }}
                                onClick={() => handleOpenRiskDetail(m)}
                              >
                                View explanation
                              </Button>

                              <Button
                                size="small"
                                color="inherit"
                                sx={{
                                  textTransform: "none",
                                  fontSize: 12,
                                }}
                                onClick={() =>
                                  handleRemoveMonitor(m.entity_type, m.entity_code)
                                }
                              >
                                Remove from monitoring
                              </Button>
                            </Box>

                                    
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </SectionCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
});

export default ExFacSummaries;


export async function captureImageForComponent(ref, {
  scale = 2,
  backgroundColor = '#ffffff'
} = {}) {
  const node = ref?.current;
  if (!node) return null;

  // Ensure fonts are loaded so text renders correctly
  // (safe to no-op if unsupported)
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch {}
  }

  // Optional: temporarily hide tooltips/crosshairs/hover effects
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-export-hide', 'true');
  styleEl.textContent = `
    .recharts-tooltip-wrapper, .recharts-default-tooltip, .recharts-crosshair { display: none !important; }
    .no-export { display: none !important; }
    *:hover { pointer-events: none !important; }
  `;
  document.head.appendChild(styleEl);

  try {
    // Render entire DOM node to PNG data URL
    const dataUrl = await htmlToImage.toPng(node, {
      pixelRatio: scale,
      backgroundColor,
      cacheBust: true,
    });

    // Convert to Image object so callers can measure w/h & draw on canvas
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = dataUrl;
    });

    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;

    return { img, w, h, dataUrl };
  } finally {
    // clean up temp styles
    document.head.removeChild(styleEl);
  }
}